import { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Trash2, X } from 'lucide-react';
import type { WbsCatalogOption } from '@mct/shared';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { cn } from '../../lib/utils';
import {
  colorClassesByIndex,
  colorIndexFromName,
  pickNextFreeColorIndex,
} from '../../lib/colorPalette';
import { ColorPickerPopover } from '../common/ColorPickerPopover';

/**
 * Result of a CatalogOptionsEditor save. Same shape used for both the
 * 領域 and ベンダー catalogs — the editor itself is option-list agnostic;
 * the caller passes a noun string for the delete-tooltip and a snapshot
 * of usage counts so in-use rows are non-deletable.
 *
 * `renames` keeps map-of-original-id-to-new-label semantics (null =
 * delete) so the existing `renameWbsItemOption` migration helper works
 * without changes. Deletions of in-use ids are filtered out before save
 * (UI disables the trash) and re-checked at save time as a defence
 * against parallel edits.
 */
export interface CatalogOptionEditResult {
  options: WbsCatalogOption[];
  renames: Map<string, string | null>;
}

/**
 * Backwards-compatible alias from the area-only first iteration. Prefer
 * `CatalogOptionEditResult` for new code.
 */
export type AreaOptionEditResult = CatalogOptionEditResult;

export interface CatalogRow {
  /** Original id when this row already existed; new ids are prefixed `__new_`. */
  id: string;
  label: string;
  colorIndex: number;
  locked: boolean;
}

/** Backwards-compatible alias. */
export type AreaRow = CatalogRow;

/** Sentinel prefix on row ids that haven't been persisted yet. */
export const NEW_ROW_ID_PREFIX = '__new_';

/**
 * Pure derivation of the "save result" from the current row state. Kept
 * outside the component so it can be unit-tested without a DOM
 * environment. Returns either the structured `CatalogOptionEditResult`
 * or a localised error message (mirrors what the editor would surface).
 *
 * The `noun` is woven into delete-conflict error messages (e.g. "領域"
 * → 「会計領域」は N 件のタスクで使用中のため削除できません). Defaults
 * to the generic 「項目」.
 */
export function deriveCatalogSaveResult(
  rows: readonly CatalogRow[],
  originalRows: readonly CatalogRow[],
  usageCounts: ReadonlyMap<string, number>,
  noun: string = '項目',
): { ok: true; result: CatalogOptionEditResult } | { ok: false; error: string } {
  void noun; // currently unused in the body; reserved for richer messaging
  const trimmed = rows.map((r) => ({ ...r, label: r.label.trim() }));
  if (trimmed.some((r) => !r.label)) {
    return { ok: false, error: '空の項目は保存できません' };
  }
  const seen = new Set<string>();
  for (const r of trimmed) {
    if (seen.has(r.label)) {
      return { ok: false, error: `「${r.label}」が重複しています` };
    }
    seen.add(r.label);
  }
  const renames = new Map<string, string | null>();
  const originalById = new Map(
    originalRows
      .filter((r) => !r.id.startsWith(NEW_ROW_ID_PREFIX))
      .map((r) => [r.id, r] as const),
  );
  const keptIds = new Set(
    trimmed
      .filter((r) => !r.id.startsWith(NEW_ROW_ID_PREFIX))
      .map((r) => r.id),
  );
  for (const [id, orig] of originalById) {
    if (!keptIds.has(id)) {
      const count = usageCounts.get(orig.label) ?? 0;
      if (count > 0) {
        return {
          ok: false,
          error: `「${orig.label}」は ${count} 件のタスクで使用中のため削除できません`,
        };
      }
      renames.set(id, null);
    }
  }
  for (const r of trimmed) {
    if (r.id.startsWith(NEW_ROW_ID_PREFIX)) continue;
    if (r.locked) continue;
    const orig = originalById.get(r.id);
    if (orig && r.label !== orig.label) renames.set(r.id, r.label);
  }
  const out: WbsCatalogOption[] = trimmed.map((r) => ({
    id: r.id.startsWith(NEW_ROW_ID_PREFIX) ? r.label : r.id,
    label: r.label,
    colorIndex: r.colorIndex,
  }));
  return { ok: true, result: { options: out, renames } };
}

/** Backwards-compatible alias of the previous helper name. */
export const deriveAreaSaveResult = deriveCatalogSaveResult;

/**
 * Generic catalog editor used for both 領域 and ベンダー. Adds, on top
 * of the simple text-list editor it replaces:
 *
 *   1. Color preview chip per row.
 *   2. Color picker popover (clicking the chip).
 *   3. Auto-assignment of the next free palette slot when a new row
 *      is added.
 *   4. Delete-guard: rows whose label is referenced by ≥1 wbsItem are
 *      rendered with a disabled trash + tooltip explaining the count.
 *
 * Save semantics match the original `ColumnOptionsEditor.onSave` so
 * call-sites can keep their existing `renameWbsItemOption` migration
 * path. The mutation side re-checks usage to guard against a parallel
 * writer "filling" an entry between dialog open and save.
 */
export function CatalogOptionsEditor({
  title,
  noun = '項目',
  options,
  usageCounts,
  locked,
  onClose,
  onSave,
}: {
  title: string;
  /**
   * Used in delete-disabled tooltip: 「この {noun} は N 件のタスクに割り
   * 当てられているため…」. Defaults to 「項目」.
   */
  noun?: string;
  options: readonly WbsCatalogOption[];
  /**
   * `label -> count` snapshot taken at dialog open. Rows whose label
   * is in this map with count > 0 cannot be deleted from the UI.
   */
  usageCounts: ReadonlyMap<string, number>;
  /**
   * Labels that must not be renamed or deleted (e.g. the 5 fixed area
   * buckets). Color editing is still allowed on locked rows so the
   * user can recolour the canonical chips.
   */
  locked?: readonly string[];
  onClose: () => void;
  onSave: (result: CatalogOptionEditResult) => Promise<void> | void;
}) {
  const initialRows = useMemo<CatalogRow[]>(
    () =>
      options.map((opt) => ({
        id: opt.id,
        label: opt.label,
        colorIndex:
          typeof opt.colorIndex === 'number' && Number.isFinite(opt.colorIndex)
            ? opt.colorIndex
            : colorIndexFromName(opt.label),
        locked: locked?.includes(opt.label) ?? false,
      })),
    [options, locked],
  );
  const [rows, setRows] = useState<CatalogRow[]>(initialRows);
  const [adding, setAdding] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  /** Row index whose color popover is currently open (or null). */
  const [pickerOpen, setPickerOpen] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Esc closes the dialog; clicking outside the editor closes too. The
  // color picker is *inside* the editor so it stays open across the
  // outside-click handler.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (pickerOpen !== null) setPickerOpen(null);
        else onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, pickerOpen]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) onClose();
    };
    const t = window.setTimeout(() => {
      document.addEventListener('mousedown', onClick);
    }, 0);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('mousedown', onClick);
    };
  }, [onClose]);

  const add = () => {
    const v = adding.trim();
    if (!v) return;
    if (rows.some((r) => r.label === v)) {
      setError(`「${v}」は既に存在します`);
      return;
    }
    // Auto-assign the next free palette slot. "Free" is computed from
    // the current row set so consecutive adds in a fresh project lay
    // out a stable rainbow.
    const nextColor = pickNextFreeColorIndex(rows.map((r) => r.colorIndex));
    setRows([
      ...rows,
      {
        id: `__new_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        label: v,
        colorIndex: nextColor,
        locked: false,
      },
    ]);
    setAdding('');
    setError(null);
  };

  const updateLabel = (idx: number, value: string) => {
    setError(null);
    setRows(rows.map((r, i) => (i === idx ? { ...r, label: value } : r)));
  };

  const updateColor = (idx: number, colorIndex: number) => {
    setRows(rows.map((r, i) => (i === idx ? { ...r, colorIndex } : r)));
    setPickerOpen(null);
  };

  const remove = (idx: number) => {
    setError(null);
    setRows(rows.filter((_, i) => i !== idx));
  };

  /** Resolve "is this row deletable?" with rationale for the tooltip. */
  const deleteState = (r: CatalogRow): { disabled: boolean; reason: string } => {
    if (r.locked) {
      return { disabled: true, reason: `固定の${noun}は削除できません` };
    }
    // Brand-new (unsaved) rows can always be removed; they aren't in
    // use yet because they don't exist in storage.
    if (r.id.startsWith('__new_')) return { disabled: false, reason: '削除' };
    const count = usageCounts.get(r.label) ?? 0;
    if (count > 0) {
      return {
        disabled: true,
        reason: `この${noun}は ${count} 件のタスクに割り当てられているため削除できません。先にタスク側の${noun}を変更してください。`,
      };
    }
    return { disabled: false, reason: '削除' };
  };

  const save = async () => {
    // Defence-in-depth: the trash button is disabled for in-use rows,
    // but a parallel writer could have created an item against an
    // entry between dialog open and save — `deriveCatalogSaveResult`
    // re-checks via the same `usageCounts` snapshot before emitting
    // deletes.
    const derived = deriveCatalogSaveResult(rows, initialRows, usageCounts, noun);
    if (!derived.ok) {
      setError(derived.error);
      return;
    }
    setSaving(true);
    try {
      await onSave(derived.result);
      onClose();
    } catch (err) {
      console.error('[CatalogOptionsEditor] save failed', err);
      setError('保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      ref={containerRef}
      className="absolute right-0 top-full z-40 mt-1 w-80 rounded-md border border-slate-200 bg-white text-sm shadow-lg"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2">
        <span className="text-xs font-semibold text-slate-700">{title}</span>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-0.5 text-slate-500 hover:bg-slate-100"
          aria-label="閉じる"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="max-h-96 space-y-1.5 overflow-y-auto px-3 py-2">
        {rows.length === 0 ? (
          <div className="py-2 text-center text-xs text-slate-400">
            項目はまだありません
          </div>
        ) : null}
        {rows.map((r, idx) => {
          const del = deleteState(r);
          const swatchClasses = colorClassesByIndex(r.colorIndex);
          return (
            <div key={r.id} className="relative flex items-center gap-1">
              <button
                type="button"
                aria-label={`${r.label} の色を変更`}
                onClick={() =>
                  setPickerOpen(pickerOpen === idx ? null : idx)
                }
                className={cn(
                  'inline-flex h-6 w-12 shrink-0 items-center justify-center rounded text-[10px] font-medium ring-1 ring-inset ring-slate-200 hover:ring-slate-400',
                  swatchClasses,
                )}
                data-testid="catalog-color-swatch"
              >
                色
              </button>
              <Input
                className="flex-1 !text-xs"
                value={r.label}
                disabled={r.locked}
                onChange={(e) => updateLabel(idx, e.target.value)}
              />
              <button
                type="button"
                onClick={() => remove(idx)}
                disabled={del.disabled}
                title={del.reason}
                aria-label={del.disabled ? del.reason : '削除'}
                aria-disabled={del.disabled}
                data-testid="catalog-delete-button"
                className={cn(
                  'rounded p-1 text-slate-500 hover:bg-red-50 hover:text-red-600',
                  del.disabled
                    ? 'cursor-not-allowed opacity-30 hover:bg-transparent hover:text-slate-500'
                    : '',
                )}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
              {pickerOpen === idx ? (
                <ColorPickerPopover
                  selected={r.colorIndex}
                  onPick={(c) => updateColor(idx, c)}
                  onClose={() => setPickerOpen(null)}
                  className="left-0 top-7"
                />
              ) : null}
            </div>
          );
        })}
        <div className="flex items-center gap-1 pt-1">
          <span className="inline-block h-6 w-12 shrink-0" aria-hidden />
          <Input
            className="flex-1 !text-xs"
            placeholder="新しい項目を追加"
            value={adding}
            onChange={(e) => setAdding(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                add();
              }
            }}
          />
          <button
            type="button"
            onClick={add}
            disabled={!adding.trim()}
            className="rounded p-1 text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-30"
            aria-label="追加"
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      {error ? (
        <div className="border-t border-red-100 bg-red-50 px-3 py-1.5 text-xs text-red-700">
          {error}
        </div>
      ) : null}
      <div className="flex justify-end gap-2 border-t border-slate-200 px-3 py-2">
        <Button variant="secondary" onClick={onClose} disabled={saving}>
          キャンセル
        </Button>
        <Button onClick={save} disabled={saving}>
          {saving ? '保存中…' : '保存'}
        </Button>
      </div>
    </div>
  );
}

/** Backwards-compatible alias for the area-only first iteration. */
export const AreaOptionsEditor = CatalogOptionsEditor;
